// Archival.tsx
import axios from 'axios'
import moment from 'moment'
import { node } from 'prop-types'
import { useEffect, useState } from 'react'
import SemanticDatepicker from 'react-semantic-ui-datepickers'
import { Button, Checkbox, Divider, Dropdown, Header, Icon, Message, Segment, Transition } from 'semantic-ui-react'
import './Archival.scss'
import FluxModal from './FluxModal'
import { serverUrl } from '../../../../utilites/config'

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  key: hour,
  text: `${String(hour).padStart(2, '0')}:00 UTC`,
  value: hour
}))
const START_OF_DAY_HOUR = 0
const END_OF_DAY_HOUR = 23

interface ArchiveTimeState {
  startTime: Date | null
  endTime: Date | null
  startHour: number
  endHour: number
}

interface FluxState extends ArchiveTimeState {
  isF10: boolean
  isF30: boolean
  isRaw: boolean
  isHourly: boolean
  isDaily: boolean
}

interface BurstState extends ArchiveTimeState {
  isF10: boolean
  isF30: boolean
  isMinutelyAvg: boolean
  isExplosion: boolean
}

const INITIAL_ARCHIVE_TIME_STATE = {
  startTime: null,
  endTime: null,
  startHour: START_OF_DAY_HOUR,
  endHour: END_OF_DAY_HOUR
}

const INITIAL_FLUX_STATE: FluxState = {
  ...INITIAL_ARCHIVE_TIME_STATE,
  isF10: false,
  isF30: false,
  isRaw: false,
  isHourly: false,
  isDaily: false
}

const INITIAL_BURST_STATE: BurstState = {
  ...INITIAL_ARCHIVE_TIME_STATE,
  isF10: false,
  isF30: false,
  isMinutelyAvg: false,
  isExplosion: false
}

const createUtcDate = (date: Date, hour: number, minute: number, second: number) =>
  moment.utc({
    year: date.getFullYear(),
    month: date.getMonth(),
    date: date.getDate(),
    hour,
    minute,
    second,
    millisecond: 0
  })

export const formatArchiveTimestamp = (date: Date, hour: number, isEnd: boolean) =>
  createUtcDate(date, hour, isEnd ? 59 : 0, isEnd ? 59 : 0).format('YYYY-MM-DD[T]HH:mm:ss[Z]')

function Archival() {
  interface ChartData {
    date: number
    band: string
    fileUrl: string
    reportType: string
    dataType: string
  }

  // ---- STATE ----
  const [fluxState, setFluxState] = useState<FluxState>(INITIAL_FLUX_STATE)
  const [burstState, setBurstState] = useState<BurstState>(INITIAL_BURST_STATE)

  // Errors
  const [fluxError, setFluxError] = useState(false)
  const [fluxErrorType, setFluxErrorType] = useState(false)
  const [fluxErrorStartTime, setFluxErrorStartTime] = useState(false)

  const [burstError, setBurstError] = useState(false)
  const [burstErrorType, setBurstErrorType] = useState(false)
  const [burstErrorStartTime, setBurstErrorStartTime] = useState(false)

  // Modals
  const [isFluxModalOpen, openFluxModal] = useState(false)
  const [isBurstModalOpen, openBurstModal] = useState(false)

  // Files
  const [fluxFilesList, setFluxFilesList] = useState<ChartData[]>([])
  const [burstFilesList, setBurstFilesList] = useState<ChartData[]>([])

  const [showInfo, setShowInfo] = useState(false)

  // Spinner on Filter (bursts)
  const [isBurstLoading, setBurstLoading] = useState(false)

  // ---- HELPERS ----
  const getFluxTypes = () => {
    const typesList: string[] = []
    if (fluxState.isRaw) typesList.push('RAW')
    if (fluxState.isHourly) typesList.push('HOURLY_AVG')
    if (fluxState.isDaily) typesList.push('DAILY_AVG')
    return typesList.join(',')
  }

  const getFluxDataTypes = () => {
    const typesList: string[] = []
    if (fluxState.isF10) typesList.push('F107')
    if (fluxState.isF30) typesList.push('F30')
    return typesList.join(',')
  }

  const getBurstDataTypes = () => {
    const typesList: string[] = []
    if (burstState.isF10) typesList.push('F107')
    if (burstState.isF30) typesList.push('F30')
    return typesList.join(',')
  }

  const getBurstTypes = () => {
    const typesList: string[] = []
    if (burstState.isMinutelyAvg) typesList.push('MINUTELY_AVG')
    if (burstState.isExplosion) typesList.push('EXPLOSION')
    return typesList.join(',')
  }

  const getCurrentUtcDate = () => {
    const now = moment.utc()
    return new Date(now.year(), now.month(), now.date())
  }

  // ---- ACTIONS ----
  const downloadFluxData = () => {
    let error = false

    if (fluxState.isF10 || fluxState.isF30) setFluxError(false)
    else {
      setFluxError(true)
      error = true
    }

    if (fluxState.isRaw || fluxState.isHourly || fluxState.isDaily) setFluxErrorType(false)
    else {
      setFluxErrorType(true)
      error = true
    }

    if (fluxState.startTime) setFluxErrorStartTime(false)
    else {
      setFluxErrorStartTime(true)
      error = true
    }

    if (error) return

    const effectiveEnd = fluxState.endTime || getCurrentUtcDate()
    const params = new URLSearchParams({
      from: formatArchiveTimestamp(fluxState.startTime as Date, fluxState.startHour, false),
      to: formatArchiveTimestamp(effectiveEnd, fluxState.endHour, true),
      dataType: getFluxTypes(),
      band: getFluxDataTypes()
    })
    const fluxRequestUrl = `${serverUrl}/archive/fluxes?${params.toString()}`


    axios
      .get(fluxRequestUrl)
      .then(resp => {
        const data: ChartData[] = resp.data || []
        setFluxFilesList(data)
        openFluxModal(true)
      })
      .catch(err => {
        console.error('=== FLUX ERROR ===')
        console.error('Error:', err)
        // Optional: show a toast / message
      })
  }

  // Bursts
  const downloadBurstData = (openModal = true) => {
    let error = false

    if (openModal) setBurstLoading(true)

    if (burstState.isF10 || burstState.isF30) {
      if (openModal) setBurstError(false)
    } else {
      if (openModal) setBurstError(true)
      error = true
    }

    if (burstState.isMinutelyAvg || burstState.isExplosion) {
      if (openModal) setBurstErrorType(false)
    } else {
      if (openModal) setBurstErrorType(true)
      error = true
    }

    if (burstState.startTime) {
      if (openModal) setBurstErrorStartTime(false)
    } else {
      if (openModal) setBurstErrorStartTime(true)
      error = true
    }

    const effectiveEnd = burstState.endTime || getCurrentUtcDate()

    if (error) {
      if (openModal) setBurstLoading(false)
      return
    }

    const params = new URLSearchParams({
      from: formatArchiveTimestamp(burstState.startTime as Date, burstState.startHour, false),
      to: formatArchiveTimestamp(effectiveEnd, burstState.endHour, true),
      band: getBurstDataTypes(),
      dataType: getBurstTypes()
    })
    const burstRequestUrl = `${serverUrl}/archive/bursts?${params.toString()}`


    axios
      .get(burstRequestUrl)
      .then(resp => {
        setBurstFilesList(resp.data || [])
        if (openModal) openBurstModal(true)
      })
      .catch(err => {
        console.error('=== BURST ERROR ===')
        console.error('Error:', err)
        // Optional: error message
      })
      .finally(() => {
        if (openModal) setBurstLoading(false)
      })
  }

  // ---- EFFECTS ----
  useEffect(() => {
    downloadBurstData(false)
  }, [])

  return (
    <div className='fade-in'>
      <Header as='h1'>
        Data archive
        <Header.Subheader>
          In this tab, you can download complete raw or averaged flux data for a selected 
          time period and frequency band (F10.7 or F30). Additionally, full-resolution spectra 
          of radio bursts are available for download within a chosen time interval. Downloading 
          large datasets may take several minutes.

        </Header.Subheader>
      </Header>

      <Segment basic>
        <Button
          icon
          labelPosition='left'
          className='download_button info-toggle-button'
          onClick={() => setShowInfo(!showInfo)}
        >
          <Icon name={showInfo ? 'chevron up' : 'chevron down'} className='info-toggle-icon' />
          {showInfo ? 'Hide data structure info' : 'Show data structure info'}
        </Button>
        <Transition visible={showInfo} animation='fade down' duration={500}>
          <Segment raised padded='very' className='margin_top_15'>
            <Header as='h4'><strong>Files availability:</strong></Header>

            <p><strong>FLUX</strong> (F10.7 and F30):</p>
            <ul>
              <li>RAW — full time resolution (0.01 s sampling) (.csv)</li>
              <li>HOUR_AVG — hourly averaged flux, one value per hour for a given day (.csv)</li>
              <li>DAY_AVG — daily averaged flux, one value per day (.csv)</li>
            </ul>

            <p><strong>BURST</strong> (F10.7 and F30):</p>
            <ul>
              <li>RAW — full-resolution spectrograms in time and frequency (.csv)</li>
              <li>S_AVG — 1-second averaged spectrograms (.csv and .fits)</li>
            </ul>

            <Divider />

            <Header as='h4'>FLUX file format (raw and averaged – *.csv format):</Header>
            <div className='archival-example'>#HH MM SS MSS FLUX[SFU] DATE FREQ[MHz] SUN_RA SUN_DECL SUN_AZ SUN_EL TEL_AZ TEL_EL</div>

            <p><strong>Meaning of fields:</strong></p>
            <p>1 — Hour (HH) [UTC]</p>
            <p>2 — Minutes (MM) [UTC]</p>
            <p>3 — Seconds (SS) [UTC]</p>
            <p>4 — Milliseconds (MSS)</p>
            <p>5 — Flux [SFU]</p>
            <p>6 — Observation date (YYYY-MM-DD)</p>
            <p>7 — Observing frequency [MHz]</p>
            <p>8 — Sun right ascension (SUN_RA)</p>
            <p>9 — Sun declination (SUN_DECL)</p>
            <p>10 — Sun azimuth (SUN_AZ)</p>
            <p>11 — Sun elevation (SUN_EL)</p>
            <p>12 — Telescope azimuth (TEL_AZ)</p>
            <p>13 — Telescope elevation (TEL_EL)</p>

            <p><strong>Example (header):</strong></p>
            <div className='archival-example'>#HH MM SS MSS FLUX[SFU] 2026-02-09 2805 +322.668 -14.727 -27.857 +19.778 -27.880 +19.775</div>
            <p><strong>Example (data row):</strong></p>
            <div className='archival-example'>HH MM SS MSS FLUX[SFU]</div>

            <Divider />

            <Header as='h4'>SPECTRA file format (raw and averaged – *.csv format):</Header>
            <div className='archival-example'>#HH MM SS MSS Power_Spectrum_Density#1-#1024[SFU] DATE FREQ[MHz] SUN_RA SUN_DECL SUN_AZ SUN_EL TEL_AZ TEL_EL</div>

            <p><strong>Meaning of fields in the header:</strong></p>
            <p>1 — Hour (HH) [UTC]</p>
            <p>2 — Minutes (MM) [UTC]</p>
            <p>3 — Seconds (SS) [UTC]</p>
            <p>4 — Milliseconds (MSS)</p>
            <p>
              5 — Power_Spectrum_Density#1-#1024 [SFU]
              (label meaning: 1024 frequency channels follow in each data row)
            </p>
            <p>6 — Observation date (YYYY-MM-DD)</p>
            <p>7 — Observing frequency [MHz]</p>
            <p>8 — Sun right ascension (SUN_RA)</p>
            <p>9 — Sun declination (SUN_DECL)</p>
            <p>10 — Sun azimuth (SUN_AZ)</p>
            <p>11 — Sun elevation (SUN_EL)</p>
            <p>12 — Telescope azimuth (TEL_AZ)</p>
            <p>13 — Telescope elevation (TEL_EL)</p>

            <p><strong>Each data row contains:</strong></p>
            <p>1 — Hour (HH) [UTC]</p>
            <p>2 — Minutes (MM) [UTC]</p>
            <p>3 — Seconds (SS) [UTC]</p>
            <p>4 — Milliseconds (MSS)</p>
            <p>5–1028 — Power spectrum density values #1–#1024 [SFU] (1024 frequency channels)</p>

            <p><strong>Example (header):</strong></p>
            <div className='archival-example'>#HH MM SS MSS Power_Spectrum_Density#1-#1024[SFU] 2026-02-12 2805 +325.512 -13.789 -67.101 +0.797 -67.319 +1.143</div>
            <p><strong>Example (data row):</strong></p>
            <div className='archival-example'>HH MM SS MSS PSD#1 PSD#2 … PSD#1024</div>

            <Divider />

            <Header as='h4'>SPECTRA file format (averaged – *.fits format):</Header>
            <p>
              The SPECTRA dataset is also available as a FITS file
              containing a 2D array (dynamic spectrum: time × frequency).
              The FITS header includes a set of descriptive keywords
              with inline comments, so the meaning of each parameter
              is documented directly in the file.
            </p>

            <p><strong>The header typically contains:</strong></p>
            <p>
              1 — Mandatory FITS structural keywords
              (e.g., SIMPLE, BITPIX, NAXIS, NAXIS1, NAXIS2).
            </p>
            <p>
              2 — Axis description keywords for time and frequency
              (e.g., CTYPE1/2, CUNIT1/2, CRPIX1/2, CRVAL1/2, CDELT2).
            </p>
            <p>
              3 — Data units and scaling
              (e.g., BUNIT, SCALE, DATAMIN, DATAMAX, BSCALE, BZERO).
            </p>
            <p>
              4 — Observation metadata
              (e.g., DATE, DATE-OBS, DATE-END, OBJECT, INSTRUME,
              TELESCOP, ORIGIN, OBS_ID, OBS_MODE, OBS_QUAL).
            </p>
            <p>
              5 — Geometry and observatory position
              (e.g., SUN_RA, SUN_DECL, SUN_AZ, SUN_EL, TEL_AZ,
              TEL_EL, OBSLAT, OBSLON, OBSALT, RSUN_OBS).
            </p>
            <p>
              6 — Temperature correction, F10.7 only
              (TCORTIME, TCORTEMP, TCORCOEF) — see below.
            </p>
            <p>
              7 — Data status (DSTATUS, SCIDATA) — see below.
            </p>
            <p>
              8 — RFI level (RFIVAL, RFIBAND, RFIAZ, RFIEL) — see below.
            </p>

            <p>
              <em>
                Note: For the full and up-to-date list of header
                keywords and their descriptions, refer to the FITS
                header comments in the file itself.
              </em>
            </p>
            <Divider />

            <Header as='h4'>Temperature correction (F10.7 only):</Header>
            <p>
              F10.7 RAW flux and SPECTRA files contain values without the temperature
              correction. Each such file carries the temperature correction coefficient
              that was in effect while it was recorded. To get corrected values,
              multiply each value by the coefficient:
            </p>
            <div className='archival-example'>corrected value = value × COEFFICIENT</div>

            <p><strong>Where to find the coefficient:</strong></p>
            <ul>
              <li>
                FLUX RAW (.csv) and SPECTRA RAW and S_AVG (.csv) — header lines in the format
                #TEMP_CORR HH:MM:SS-HH:MM:SS COEFFICIENT, one per time range in which the
                coefficient did not change (times in UTC, the end is exclusive). The number of
                lines depends on how often the coefficient was updated. For a data row use the
                line whose range contains the time of the row.
              </li>
              <li>
                SPECTRA S_AVG (.fits) — keywords TCORTIME, TCORTEMP, TCORCOEF of the coefficient
                in effect at the start of the file; multiply the array values by TCORCOEF.
              </li>
            </ul>

            <p><strong>Example:</strong></p>
            <div className='archival-example'>#TEMP_CORR 07:00:00-07:15:00 0.89065</div>
            <div className='archival-example'>#TEMP_CORR 07:15:00-08:00:00 0.90349</div>
            <div className='archival-example'>07 20 00 155 0.141807 → 0.141807 × 0.90349 = 0.128121</div>

            <ul>
              <li>HOUR_AVG and DAY_AVG flux files are already corrected — do not multiply them again.</li>
              <li>
                F30 files and F10.7 files without #TEMP_CORR lines carry no coefficient —
                use their values as they are.
              </li>
            </ul>

            <Divider />

            <Header as='h4'>Data status:</Header>
            <p>
              The telescope reports what it is doing: observing the Sun (Sun Dataflow), measuring
              radio interference (RFI Dataflow) or taking correction measurements (Correction
              measurements). <strong>Only Sun Dataflow is science data.</strong> RFI and correction
              measurements must not be used as solar flux.
            </p>

            <p><strong>Where to find the data status:</strong></p>
            <ul>
              <li>
                FLUX RAW, SPECTRA RAW and S_AVG (.csv) — header lines #DATA_STATUS HH:MM:SS STATUS
                (time in UTC), one per status change within the file; a status is valid from its
                time until the next line. They are followed by one #SCIENCE_DATA line.
              </li>
              <li>SPECTRA S_AVG (.fits) — keywords DSTATUS (statuses in the file) and SCIDATA.</li>
            </ul>

            <p><strong>#SCIENCE_DATA / SCIDATA values:</strong></p>
            <ul>
              <li>YES — the whole file is Sun Dataflow.</li>
              <li>NO — the whole file is RFI Dataflow or Correction measurements; not science data.</li>
              <li>
                PARTIAL — the file mixes science and non-science periods; use only the rows recorded
                under Sun Dataflow.
              </li>
              <li>UNKNOWN — the status is not known for the whole file.</li>
            </ul>

            <p><strong>Example:</strong></p>
            <div className='archival-example'>#DATA_STATUS 06:00:00 Sun Dataflow</div>
            <div className='archival-example'>#DATA_STATUS 06:42:10 RFI Dataflow</div>
            <div className='archival-example'>
              #SCIENCE_DATA PARTIAL - RFI Dataflow and Correction measurements are not science data
            </div>

            <ul>
              <li>
                Files without #DATA_STATUS lines were archived before the status was recorded, or
                no status was reported for them. HOUR_AVG and DAY_AVG files carry no data status.
              </li>
            </ul>

            <Divider />

            <Header as='h4'>Radio interference (RFI) at the Sun position:</Header>
            <p>
              The radio interference around the telescope is mapped over the whole sky by RFI scans.
              Archived files carry the interference level from that map at the position of the Sun,
              where the telescope was pointing, averaged over a time window. It tells how much
              interference to expect in the data; it is not a measurement taken during the observation.
              The level is given only for times under Sun Dataflow — at other times the telescope
              does not point at the Sun.
            </p>

            <p><strong>Where to find the RFI level:</strong></p>
            <ul>
              <li>
                FLUX RAW, SPECTRA RAW and S_AVG (.csv) — header lines
                #RFI HH:MM:SS-HH:MM:SS AZ=AZIMUTH EL=ELEVATION BAND=LEVEL (times in UTC, the end is
                exclusive; mean Sun azimuth from north and elevation in degrees). FLUX RAW files have
                one line per 4 minutes, SPECTRA files one line for their 5 minutes. F10.7 files give
                the F10 level, F30 files the F30 level.
              </li>
              <li>
                SPECTRA S_AVG (.fits) — keywords RFIVAL (level), RFIBAND, RFIAZ and RFIEL.
              </li>
            </ul>

            <p><strong>Example:</strong></p>
            <div className='archival-example'>#RFI 06:00:00-06:04:00 AZ=120.71 EL=15.82 F10=12.40</div>
            <div className='archival-example'>#RFI 06:04:00-06:08:00 AZ=121.48 EL=16.31 F10=12.90</div>

            <ul>
              <li>
                A missing line means the telescope was not observing the Sun in that window, or the
                RFI map had no data at the Sun position. HOUR_AVG and DAY_AVG files carry no RFI level.
              </li>
            </ul>
          </Segment>
        </Transition>
      </Segment>

      {/* ======= Solar flux ======= */}
      <Header as='h3'>Solar flux</Header>
      <Header.Subheader>Please select the timeframe:</Header.Subheader>

      {/* Dates */}
      <div className='archive-time-row'>
        <div className='archive-time-field'>
          <Header as='h4' className='margin_bottom_5'>
            Start time
          </Header>
          <SemanticDatepicker
            format='DD-MM-YYYY'
            maxDate={fluxState.endTime || new Date()}
            onChange={(e, d) => setFluxState(prev => ({
              ...prev,
              startTime: d.value ? (Array.isArray(d.value) ? d.value[0] : d.value) : null
            }))}
          />
          <Dropdown
            selection
            placeholder='Start hour (UTC)'
            options={HOUR_OPTIONS}
            value={fluxState.startHour}
            onChange={(e, d) => setFluxState(prev => ({ ...prev, startHour: Number(d.value) }))}
            className='archive-hour-dropdown'
            data-testid='start-hour-flux'
          />
          <Transition animation='slide down' duration={300} visible={fluxErrorStartTime} unmountOnHide>
            <Message negative size='small' onDismiss={() => setFluxErrorStartTime(false)} style={{ marginTop: 8 }}>
              <Message.Content>You have to select start time date.</Message.Content>
            </Message>
          </Transition>
        </div>

        <div className='archive-time-field'>
          <Header as='h4' className='margin_bottom_5'>
            End time
          </Header>
          <SemanticDatepicker
            format='DD-MM-YYYY'
            minDate={fluxState.startTime || undefined}
            maxDate={new Date()}
            value={fluxState.endTime}
            onChange={(e, d) => setFluxState(prev => ({
              ...prev,
              endTime: d.value ? (Array.isArray(d.value) ? d.value[0] : d.value) : null
            }))}
          />
          <Dropdown
            selection
            placeholder='End hour (UTC)'
            options={HOUR_OPTIONS}
            value={fluxState.endHour}
            onChange={(e, d) => setFluxState(prev => ({ ...prev, endHour: Number(d.value) }))}
            className='archive-hour-dropdown'
            data-testid='end-hour-flux'
          />
        </div>
      </div>

      {/* Bands */}
      <Header as='h4'>Select at least one</Header>
      <div className='flex_row'>
        <Checkbox label='F10.7' onChange={(e, d) => setFluxState(prev => ({ ...prev, isF10: !!d.checked }))} />
        <Checkbox
          label='F30'
          className='margin_left_50'
          onChange={(e, d) => setFluxState(prev => ({ ...prev, isF30: !!d.checked }))}
        />
      </div>
      <Transition animation='slide down' duration={300} visible={fluxError} unmountOnHide>
        <Message negative size='small' onDismiss={() => setFluxError(false)} style={{ marginTop: 8, maxWidth: 560 }}>
          <Message.Content>You have to select at least one bandpass.</Message.Content>
        </Message>
      </Transition>

      {/* Data type */}
      <Header as='h4'>Select data type</Header>
      <div className='flex_row'>
        <Checkbox label='raw data' onChange={(e, d) => setFluxState(prev => ({ ...prev, isRaw: !!d.checked }))} />
        <Checkbox
          label='hourly averages'
          className='margin_left_50'
          onChange={(e, d) => setFluxState(prev => ({ ...prev, isHourly: !!d.checked }))}
        />
        <Checkbox
          label='daily averages'
          className='margin_left_50'
          onChange={(e, d) => setFluxState(prev => ({ ...prev, isDaily: !!d.checked }))}
        />
      </div>
      <Transition animation='slide down' duration={300} visible={fluxErrorType} unmountOnHide>
        <Message
          negative
          size='small'
          onDismiss={() => setFluxErrorType(false)}
          style={{ marginTop: 8, maxWidth: 680 }}
        >
          <Message.Content>You have to select at least one data type.</Message.Content>
        </Message>
      </Transition>

      {/* Główny Download — ten sam kolor jak Filter dzięki klasie */}
      <Button
        className='download_button'
        onClick={downloadFluxData}
        icon
        labelPosition='left'
        style={{ marginTop: 12 }}
      >
        <Icon name='download' /> Download
      </Button>

      <Divider className='margin_top_40' />

      {/* ======= Radio Spectrograms ======= */}
      <Header as='h3'>Radio Spectrograms</Header>
      <Header.Subheader>Please select the timeframe:</Header.Subheader>

      {/* Dates */}
      <div className='archive-time-row'>
        <div className='archive-time-field'>
          <Header as='h4' className='margin_bottom_5'>
            Start time
          </Header>
          <SemanticDatepicker
            format='DD-MM-YYYY'
            maxDate={burstState.endTime && burstState.endTime < new Date() ? burstState.endTime : new Date()}
            value={burstState.startTime}
            onChange={(e, d) => setBurstState(prev => ({
              ...prev,
              startTime: d.value ? (Array.isArray(d.value) ? d.value[0] : d.value) : null
            }))}
          />
          <Dropdown
            selection
            placeholder='Start hour (UTC)'
            options={HOUR_OPTIONS}
            value={burstState.startHour}
            onChange={(e, d) => setBurstState(prev => ({ ...prev, startHour: Number(d.value) }))}
            className='archive-hour-dropdown'
            data-testid='start-hour-burst'
          />
          <Transition animation='slide down' duration={300} visible={burstErrorStartTime} unmountOnHide>
            <Message negative size='small' onDismiss={() => setBurstErrorStartTime(false)} style={{ marginTop: 8 }}>
              <Message.Content>You have to select start time date.</Message.Content>
            </Message>
          </Transition>
        </div>

        <div className='archive-time-field'>
          <Header as='h4' className='margin_bottom_5'>
            End time
          </Header>
          <SemanticDatepicker
            format='DD-MM-YYYY'
            minDate={burstState.startTime || undefined}
            maxDate={new Date()}
            value={burstState.endTime}
            clearable
            onChange={(e, d) => setBurstState(prev => ({
              ...prev,
              endTime: d.value ? (Array.isArray(d.value) ? d.value[0] : d.value) : null
            }))}
          />
          <Dropdown
            selection
            placeholder='End hour (UTC)'
            options={HOUR_OPTIONS}
            value={burstState.endHour}
            onChange={(e, d) => setBurstState(prev => ({ ...prev, endHour: Number(d.value) }))}
            className='archive-hour-dropdown'
            data-testid='end-hour-burst'
          />
        </div>
      </div>

      {/* Bands */}
      <Header as='h4'>Select at least one</Header>
      <div className='flex_row'>
        <Checkbox
          label='F10.7'
          checked={burstState.isF10}
          onChange={(e, d) => setBurstState(prev => ({ ...prev, isF10: !!d.checked }))}
        />
        <Checkbox
          label='F30'
          className='margin_left_50'
          checked={burstState.isF30}
          onChange={(e, d) => setBurstState(prev => ({ ...prev, isF30: !!d.checked }))}
        />
      </div>
      <Transition animation='slide down' duration={300} visible={burstError} unmountOnHide>
        <Message negative size='small' onDismiss={() => setBurstError(false)} style={{ marginTop: 8, maxWidth: 560 }}>
          <Message.Content>You have to select at least one bandpass.</Message.Content>
        </Message>
      </Transition>

      {/* Data type */}
      <Header as='h4'>Select data type</Header>
      <div className='flex_row'>
        <Checkbox
          label='averaged data'
          onChange={(e, d) => setBurstState(prev => ({ ...prev, isMinutelyAvg: !!d.checked }))}
        />
        <Checkbox
          label='raw burst'
          className='margin_left_50'
          onChange={(e, d) => setBurstState(prev => ({ ...prev, isExplosion: !!d.checked }))}
        />
      </div>
      <Transition animation='slide down' duration={300} visible={burstErrorType} unmountOnHide>
        <Message
          negative
          size='small'
          onDismiss={() => setBurstErrorType(false)}
          style={{ marginTop: 8, maxWidth: 680 }}
        >
          <Message.Content>You have to select at least one data type.</Message.Content>
        </Message>
      </Transition>

      <Button
        secondary
        className='download_button'
        onClick={() => downloadBurstData(true)}
        icon
        labelPosition='left'
        style={{ marginTop: 12 }}
        loading={isBurstLoading}
        disabled={isBurstLoading}
      >
        <Icon name='filter' /> Filter
      </Button>

      {/* FLUX selection modal (existing) */}
      <FluxModal setOpen={openFluxModal} open={isFluxModalOpen} files={fluxFilesList} />

      {/* BURST modal */}
      <FluxModal setOpen={openBurstModal} open={isBurstModalOpen} files={burstFilesList} />
    </div>
  )
}

Archival.propTypes = { children: node }

export default Archival
